// src/components/Gallery.js
import React from "react";
import "../styles/Gallery.css";

const Gallery = ({ language = "fr" }) => {
  const [currentImage, setCurrentImage] = React.useState(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Images en stack vertical avec captions et layout spéciaux
  const images = [
    {
      src: "/1.webp",
      layout: "single",
    },
    {
      src: "/2.webp",
      layout: "dual-left",
    },
    {
      src: "/3.webp",
      layout: "dual-right",
    },
    {
      src: "/6.webp",
      layout: "single",
    },
    {
      src: "/5.webp",
      layout: "single",
    },
  ];

  // Traductions avec support allemand
  const translations = {
    en: {
      title: "Photo Gallery",
      subtitle: "Discover Le Loft Molière",
      backToHome: "← Back",
      expandTooltip: "Expand",
    },
    fr: {
      title: "Galerie Photos",
      subtitle: "Découvrez Le Loft Molière",
      backToHome: "← Retour",
      expandTooltip: "Agrandir",
    },
    de: {
      title: "Fotogalerie",
      subtitle: "Entdecken Sie Le Loft Molière",
      backToHome: "← Zurück",
      expandTooltip: "Vergrößern",
    },
  };

  const t = translations[language] || translations.fr; // Fallback vers français

  const openFullscreen = React.useCallback((index) => {
    setCurrentImage(index);
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = React.useCallback(() => {
    setIsFullscreen(false);
    setCurrentImage(null);
  }, []);

  // Navigation au clavier
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isFullscreen) return;

      if (e.key === "Escape") {
        closeFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isFullscreen, closeFullscreen]);

  // Modal plein écran
  if (isFullscreen && currentImage !== null) {
    const image = images[currentImage];

    return (
      <div className="gallery-fullscreen" onClick={closeFullscreen}>
        {/* Bouton de fermeture */}
        <button onClick={closeFullscreen} className="gallery-fullscreen-close">
          ×
        </button>

        {/* Image plein écran */}
        <img
          src={image.src}
          alt={image.alt}
          onClick={(e) => e.stopPropagation()}
          className="gallery-fullscreen-image"
        />

        {/* Caption en fullscreen */}
        <div className="gallery-fullscreen-caption">
          <div className="gallery-fullscreen-caption-title">{image.title}</div>
          <div className="gallery-fullscreen-caption-desc">
            {image.description}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      <div className="gallery-wrapper">
        {/* Stack vertical d'images */}
        <div className="gallery-stack">
          {images.map((image, index) => {
            // Skip dual-right images as they're handled with dual-left
            if (image.layout === "dual-right") return null;

            return (
              <div
                key={index}
                className={`gallery-item gallery-item--${image.layout}`}
              >
                {image.layout === "dual-left" ? (
                  // Dual layout pour images portraits côte à côte
                  <div className="gallery-dual-container">
                    {/* Image de gauche */}
                    <div className="gallery-dual-item">
                      <div
                        className="gallery-image-container"
                        onClick={() => openFullscreen(index)}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="gallery-image gallery-image--portrait"
                        />
                        <div className="gallery-number">
                          {String(index + 1).padStart(2, "0")}
                        </div>
                        <div
                          className="gallery-expand-btn"
                          title={t.expandTooltip}
                        >
                          ⛶
                        </div>
                      </div>
                      <div className="gallery-caption">
                        <h3 className="gallery-caption-title">{image.title}</h3>
                        <p className="gallery-caption-desc">
                          {image.description}
                        </p>
                      </div>
                    </div>

                    {/* Image de droite */}
                    {images[index + 1] &&
                      images[index + 1].layout === "dual-right" && (
                        <div className="gallery-dual-item">
                          <div
                            className="gallery-image-container"
                            onClick={() => openFullscreen(index + 1)}
                          >
                            <img
                              src={images[index + 1].src}
                              alt={images[index + 1].alt}
                              className="gallery-image gallery-image--portrait"
                            />
                            <div className="gallery-number">
                              {String(index + 2).padStart(2, "0")}
                            </div>
                            <div
                              className="gallery-expand-btn"
                              title={t.expandTooltip}
                            >
                              ⛶
                            </div>
                          </div>
                          <div className="gallery-caption">
                            <h3 className="gallery-caption-title">
                              {images[index + 1].title}
                            </h3>
                            <p className="gallery-caption-desc">
                              {images[index + 1].description}
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  // Layout single normal
                  <>
                    <div
                      className="gallery-image-container"
                      onClick={() => openFullscreen(index)}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="gallery-image"
                      />
                      <div className="gallery-number">
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div
                        className="gallery-expand-btn"
                        title={t.expandTooltip}
                      >
                        ⛶
                      </div>
                    </div>
                    <div className="gallery-caption">
                      <h3 className="gallery-caption-title">{image.title}</h3>
                      <p className="gallery-caption-desc">
                        {image.description}
                      </p>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Gallery;
